import { useEffect, useState } from "react";
import { Grid, Switch, FormControlLabel } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { GeneralModal } from ".";
import {
  ButtonWithLoadingIndicator,
  CancelButton,
  TextFieldInput,
} from "../components-atoms";
import { ITheme } from "../constants/theme";
import { makeStyles } from "@material-ui/styles";
import { ProOrganisationDetails } from "../redux/actions/subscriptions";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: EditProOrganisationData) => void;
  organisation?: ProOrganisationDetails;
  loading?: boolean;
}

export interface EditProOrganisationData {
  Name: string;
  PhoneNumber: string;
  VATNumber: string;
  IsCollectEnabled: boolean;
}

export const EditProOrganisationModal = ({
  visible,
  onClose,
  onSubmit,
  organisation,
  loading = false
}: IProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<EditProOrganisationData>({
    Name: "",
    PhoneNumber: "",
    VATNumber: "",
    IsCollectEnabled: false,
  });

  useEffect(() => {
    if (organisation) {
      setFormData({
        Name: organisation.Name || "",
        PhoneNumber: organisation.PhoneNumber || "",
        VATNumber: organisation.VATNumber || "",
        IsCollectEnabled: organisation.IsCollectEnabled || false,
      });
    }
  }, [organisation]);

  const handleInputChange = (field: keyof EditProOrganisationData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const onFormSubmit = () => {
    onSubmit(formData);
  };

  const handleClose = () => {
    // Reset form data to original values
    if (organisation) {
      setFormData({
        Name: organisation.Name || "",
        PhoneNumber: organisation.PhoneNumber || "",
        VATNumber: organisation.VATNumber || "",
        IsCollectEnabled: organisation.IsCollectEnabled || false,
      });
    }
    onClose();
  };

  return (
    <GeneralModal
      title={t('editProOrganisation.title')}
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextFieldInput
                  label={t('editProOrganisation.organisationName')}
                  value={formData.Name}
                  onChange={handleInputChange('Name')}
                  placeholder={t('editProOrganisation.organisationNamePlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label={t('editProOrganisation.phoneNumber')}
                  value={formData.PhoneNumber}
                  onChange={handleInputChange('PhoneNumber')}
                  placeholder={t('editProOrganisation.phoneNumberPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label={t('editProOrganisation.vatNumber')}
                  value={formData.VATNumber}
                  onChange={handleInputChange('VATNumber')}
                  placeholder={t('editProOrganisation.vatNumberPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.IsCollectEnabled}
                      onChange={handleInputChange('IsCollectEnabled')}
                      color="primary"
                    />
                  }
                  label={t('editProOrganisation.tkdEnabled')}
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <CancelButton onClick={handleClose} />
          <ButtonWithLoadingIndicator
            onClick={onFormSubmit}
            disabled={loading || !formData.Name.trim()}
            title={t('editProOrganisation.saveChanges')}
            type="submit"
            loading={loading}
          />
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  fieldset: {
    margin: "16px 0 0",
    padding: 0,
    border: 0,
  },
}));
